import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { appRouter } from '@/App';
import { createQueryClient } from '@/lib/query/query-client';
import { setApiErrorHandler } from '@/lib/api/client';
import { clearAdminSession } from '@/lib/auth/admin-auth';
import { clearTenantSession } from '@/lib/auth/tenant-auth';
import { bootstrapTenantSessionFromUrl } from '@/lib/auth/bootstrap-url-session';
import { redirectToLoginWithReturn } from '@/lib/auth/session-redirect';
import { ThemeProvider } from '@/components/providers/ThemeProvider';
import { TimezoneProvider } from '@/components/providers/TimezoneProvider';
import { ToastProvider, useToast } from '@/hooks/useToast';
import { ErrorBoundary } from '@/components/feedback/ErrorBoundary';
import { OfflineBanner } from '@/components/feedback/OfflineBanner';
import { ToastViewport } from '@/components/feedback/Toast';
import { clearChunkReloadFlag } from '@/lib/lazy-retry';
import '@/styles/theme.css';
import '@/styles/index.css';

clearChunkReloadFlag();

const queryClient = createQueryClient();

bootstrapTenantSessionFromUrl();

function ApiErrorBridge() {
  const { pushToast } = useToast();

  useEffect(() => {
    setApiErrorHandler((error, portal) => {
      if (error.status === 401) {
        if (portal === 'admin') {
          clearAdminSession();
          redirectToLoginWithReturn('admin');
          return;
        }
        if (portal === 'tenant') {
          clearTenantSession();
          redirectToLoginWithReturn('tenant');
          return;
        }
      }

      if (error.status === 403) {
        pushToast({
          variant: 'error',
          title: 'Permission denied',
          description: error.message,
        });
        return;
      }

      if (error.status >= 500 || error.status === 0) {
        pushToast({
          variant: 'error',
          title: 'Request failed',
          description: error.message,
        });
      }
    });

    return () => setApiErrorHandler(undefined);
  }, [pushToast]);

  return null;
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <TimezoneProvider>
            <ToastProvider>
              <ApiErrorBridge />
              <OfflineBanner />
              <RouterProvider router={appRouter} />
              <ToastViewport />
            </ToastProvider>
          </TimezoneProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
